import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Send, Download, Sparkles, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { API, BACKEND_URL, auth, inputClass, labelClass, primaryBtn, ghostBtn, handleAuthError } from "./shared";

const fmt = (v) => `${Math.round(v).toLocaleString("fr-FR")} FCFA`;
const emptyItem = () => ({ description: "", qty: 1, unit_price: "" });

const EstimateDialog = ({ quote, token, logout, onSaved }) => {
  const est = quote.estimate;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(est?.items?.length ? est.items : [emptyItem()]);
  const [notes, setNotes] = useState(est?.notes || "");
  const [validity, setValidity] = useState(est?.validity_days || 15);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [hints, setHints] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const suggest = async () => {
    setAiLoading(true);
    try {
      const r = await axios.post(`${API}/quotes/${quote.id}/estimate/suggest`, { hints }, auth(token));
      const filled = items.filter((it) => it.description.trim());
      setItems([...filled, ...r.data.items]);
      if (r.data.notes && !notes.trim()) setNotes(r.data.notes);
      toast.success(`${r.data.items.length} lignes proposées par l'IA : vérifiez les prix avant d'envoyer`);
    } catch (err) {
      const d = err.response?.data?.detail;
      handleAuthError(err, logout, () => toast.error(typeof d === "string" ? d : "L'assistant IA est indisponible"));
    } finally {
      setAiLoading(false);
    }
  };

  const total = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const update = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));

  const save = async () => {
    const clean = items.filter((it) => it.description.trim()).map((it) => ({ description: it.description.trim(), qty: Number(it.qty) || 1, unit_price: Number(it.unit_price) || 0 }));
    if (!clean.length) return toast.error("Ajoutez au moins une ligne");
    setSaving(true);
    try {
      await axios.post(`${API}/quotes/${quote.id}/estimate`, { items: clean, notes, validity_days: Number(validity) || 15 }, auth(token));
      toast.success("Devis chiffré enregistré");
      onSaved();
      return true;
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de l'enregistrement"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    setSending(true);
    try {
      if (!(await save())) return;
      await axios.post(`${API}/quotes/${quote.id}/estimate/send`, {}, auth(token));
      toast.success(`Devis envoyé à ${quote.email}`);
      onSaved();
      setOpen(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      handleAuthError(err, logout, () => toast.error(typeof d === "string" ? d : "Échec de l'envoi"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`estimate-open-${quote.id}`} className={`${ghostBtn} ${est ? "border-gold text-gold" : ""}`}>
          <FileText className="h-3.5 w-3.5" /> {est ? `Devis ${fmt(est.total)}${est.sent_at ? " · envoyé ✓" : ""}` : "Devis chiffré PDF"}
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl border-brand/10 bg-white p-8 sm:max-w-3xl" data-testid="estimate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-forest">Devis chiffré — {quote.name}</DialogTitle>
          <DialogDescription className="text-brand-hover">{quote.service}{quote.vehicle ? ` · ${quote.vehicle}` : ""}{est ? ` · N° ${est.number}` : ""}</DialogDescription>
        </DialogHeader>

        <div className="mt-4 rounded-2xl border border-gold/30 bg-cream p-4" data-testid="estimate-ai-box">
          <p className="text-xs leading-relaxed text-forest"><Sparkles className="mr-1 inline h-3.5 w-3.5 text-gold" />L'IA propose des lignes et des prix indicatifs à partir de la demande du client{quote.message ? "" : " (aucune description fournie)"}. Vous restez maître du devis : modifiez tout avant d'enregistrer.</p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <input value={hints} onChange={(e) => setHints(e.target.value)} placeholder="Indications (optionnel) : ex. remplacer disques + plaquettes, peinture pare-chocs" data-testid="estimate-ai-hints" className={inputClass} />
            <button type="button" onClick={suggest} disabled={aiLoading} data-testid="estimate-ai-suggest" className={`${primaryBtn} shrink-0`}>
              {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {aiLoading ? "Analyse..." : "Proposer avec l'IA"}
            </button>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <div className="hidden grid-cols-12 gap-2 font-mono text-[10px] uppercase tracking-widest text-brand sm:grid">
            <span className="col-span-6">Désignation</span><span className="col-span-2">Qté</span><span className="col-span-3">Prix unitaire (FCFA)</span>
          </div>
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2" data-testid={`estimate-item-${i}`}>
              <input value={it.description} onChange={(e) => update(i, "description", e.target.value)} placeholder="Ex : Remplacement plaquettes de frein" data-testid={`estimate-item-${i}-description`} className={`${inputClass} col-span-12 sm:col-span-6`} />
              <input type="number" min="0.5" step="0.5" value={it.qty} onChange={(e) => update(i, "qty", e.target.value)} data-testid={`estimate-item-${i}-qty`} className={`${inputClass} col-span-4 sm:col-span-2`} />
              <input type="number" min="0" step="500" value={it.unit_price} onChange={(e) => update(i, "unit_price", e.target.value)} placeholder="0" data-testid={`estimate-item-${i}-price`} className={`${inputClass} col-span-6 sm:col-span-3`} />
              <button type="button" onClick={() => setItems(items.length > 1 ? items.filter((_, idx) => idx !== i) : [emptyItem()])} data-testid={`estimate-item-${i}-remove`} className="col-span-2 flex items-center justify-center rounded-xl border border-brand/20 text-brand hover:border-gold hover:text-gold sm:col-span-1" aria-label="Retirer">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => setItems([...items, emptyItem()])} data-testid="estimate-add-item" className={ghostBtn}>
            <Plus className="h-3.5 w-3.5" /> Ajouter une ligne
          </button>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <label className={labelClass}>Remarques (optionnel)</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="estimate-notes" className={`${inputClass} resize-none`} placeholder="Acompte, délais, garantie..." />
          </div>
          <div>
            <label className={labelClass}>Validité (jours)</label>
            <input type="number" min="1" value={validity} onChange={(e) => setValidity(e.target.value)} data-testid="estimate-validity" className={inputClass} />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-cream p-5">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-brand">Total</p>
            <p className="font-display text-3xl font-extrabold text-gold" data-testid="estimate-total">{fmt(total)}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {est && (
              <a href={`${BACKEND_URL}/api/quotes/${quote.id}/estimate.pdf?key=${est.pdf_key}`} target="_blank" rel="noopener noreferrer" data-testid="estimate-download" className={ghostBtn}>
                <Download className="h-3.5 w-3.5" /> PDF
              </a>
            )}
            <button type="button" onClick={save} disabled={saving} data-testid="estimate-save" className={ghostBtn}>
              {saving ? "..." : "Enregistrer"}
            </button>
            <button type="button" onClick={send} disabled={sending || !quote.email} title={quote.email ? `Envoyer à ${quote.email}` : "Le client n'a pas renseigné d'email"} data-testid="estimate-send" className={primaryBtn}>
              <Send className="h-4 w-4" /> {sending ? "Envoi..." : "Envoyer au client"}
            </button>
          </div>
        </div>
        {!quote.email && <p className="mt-2 text-xs text-gold">Ce client n'a pas laissé d'email : téléchargez le PDF et envoyez-le sur WhatsApp.</p>}
      </DialogContent>
    </Dialog>
  );
};

export default EstimateDialog;
